import React, { useState, useEffect, useCallback } from "react";
import axios from "../../axios";
import { IService } from "../../interfaces/domain";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faSync,
  faCheck,
  faTimes,
  faBuilding,
  faShieldAlt,
  faEnvelope,
  faPhone,
  faGlobe,
  faExclamationTriangle,
  faSpinner,
  faAngleDown,
  faAngleUp,
  faSearch,
  faMapMarkerAlt,
  faPlus,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";
import {
  ISupplyPipeline,
  ILead,
  approveLead,
  rejectLead,
  reevaluateLead,
  listSupplyPipelines,
  getSupplyPipelineLeads,
  startSupplyPipeline,
  listKnowledgeBases,
  IServiceKnowledgeBase,
} from "../../services/OnboardingService";
import classes from "./Onboarding.module.scss";

interface ISupplyPipelineTabProps {
  onSelectLead?: (lead: ILead) => void;
  onNavigateToKb?: (serviceToken: string) => void;
}

export const SupplyPipelineTab: React.FC<ISupplyPipelineTabProps> = ({ onSelectLead, onNavigateToKb }) => {
  const [pipelines, setPipelines] = useState<ISupplyPipeline[]>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [pipelinesError, setPipelinesError] = useState<string | null>(null);

  // New pipeline form
  const [showNewForm, setShowNewForm] = useState(false);
  const [serviceToken, setServiceToken] = useState("");
  const [services, setServices] = useState<IService[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<IServiceKnowledgeBase[]>([]);
  const [regions, setRegions] = useState("");
  const [tolCodes, setTolCodes] = useState("");
  const [placesQueries, setPlacesQueries] = useState("");
  const [evaluationCriteria, setEvaluationCriteria] = useState("");
  const [startingPipeline, setStartingPipeline] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Selected pipeline & leads
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [leads, setLeads] = useState<ILead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leadsError, setLeadsError] = useState<string | null>(null);
  const [scoreFilter, setScoreFilter] = useState<string>("");
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);

  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId);

  // Action feedback
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // AI Re-evaluation
  const [reevalPrompts, setReevalPrompts] = useState<Record<string, string>>({});
  const [reevalLoadingId, setReevalLoadingId] = useState<string | null>(null);

  // Load pipelines
  const fetchPipelines = useCallback(async () => {
    setLoadingPipelines(true);
    setPipelinesError(null);
    try {
      const data = await listSupplyPipelines();
      // Sort newest first
      const sorted = [...data].sort((a, b) => {
        const tA = a.created || 0;
        const tB = b.created || 0;
        return tB - tA;
      });
      setPipelines(sorted);
    } catch (err: any) {
      console.error("Error fetching pipelines:", err);
      setPipelinesError("Virhe ladattaessa hakuajoja.");
    } finally {
      setLoadingPipelines(false);
    }
  }, []);

  useEffect(() => {
    fetchPipelines();
  }, [fetchPipelines]);

  useEffect(() => {
    const fetchServicesAndKBs = async () => {
      try {
        const res = await axios.post("/json/services/list", {});
        setServices(res.data || []);
        
        const kbList = await listKnowledgeBases();
        setKnowledgeBases(kbList || []);
      } catch (err) {
        console.error("Error fetching services or KBs:", err);
      }
    };
    fetchServicesAndKBs();
  }, []);

  // Load leads for selected pipeline
  const fetchLeads = useCallback(async (pipelineId: string, score: string) => {
    setLoadingLeads(true);
    setLeadsError(null);
    try {
      const response = await getSupplyPipelineLeads(pipelineId, score || undefined);
      setLeads(response.leads || []);
    } catch (err) {
      console.error("Error fetching pipeline leads:", err);
      setLeadsError("Virhe ladattaessa ehdokaslistaa.");
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPipelineId) {
      fetchLeads(selectedPipelineId, scoreFilter);
    } else {
      setLeads([]);
    }
  }, [selectedPipelineId, scoreFilter, fetchLeads]);

  // Handle pipeline start
  const handleStartPipeline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceToken.trim()) {
      setFormError("Palvelutunniste (Service Token) on pakollinen.");
      return;
    }
    setStartingPipeline(true);
    setFormError(null);
    try {
      await startSupplyPipeline({
        serviceToken: serviceToken.trim(),
        regions: regions.trim() || undefined,
        tolCodes: tolCodes.trim() || undefined,
        placesQueries: placesQueries.trim() || undefined,
        evaluationCriteria: evaluationCriteria.trim() || undefined,
      });
      // Reset form
      setServiceToken("");
      setRegions("");
      setTolCodes("");
      setPlacesQueries("");
      setEvaluationCriteria("");
      setShowNewForm(false);
      // Reload
      fetchPipelines();
    } catch (err: any) {
      console.error("Error starting pipeline:", err);
      setFormError("Virhe käynnistettäessä automaattista hakuajoa.");
    } finally {
      setStartingPipeline(false);
    }
  };

  // Handle lead approval
  const handleApproveLead = async (leadId: string) => {
    setActionLoadingId(leadId);
    try {
      const result = await approveLead(leadId);
      if (result.success) {
        // Update local state to show approved/linked state
        setLeads((prevLeads) =>
          prevLeads.map((l) =>
            l.id === leadId
              ? { ...l, linkedOrgId: result.organizationId, status: "CONTRACT" }
              : l
          )
        );
      }
    } catch (err) {
      console.error("Error approving lead:", err);
      alert("Virhe hyväksyttäessä ehdokasta.");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Handle lead rejection
  const handleRejectLead = async (leadId: string) => {
    if (!window.confirm("Haluatko varmasti hylätä tämän ehdokkaan?")) {
      return;
    }
    setActionLoadingId(leadId);
    try {
      const result = await rejectLead(leadId);
      if (result.success) {
        // Remove from local list or update status
        setLeads((prevLeads) => prevLeads.filter((l) => l.id !== leadId));
      }
    } catch (err) {
      console.error("Error rejecting lead:", err);
      alert("Virhe hylättäessä ehdokasta.");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Handle single lead manual AI re-evaluation
  const handleReevaluateLead = async (leadId: string) => {
    const prompt = reevalPrompts[leadId] || "";
    setReevalLoadingId(leadId);
    try {
      const updatedLead = await reevaluateLead(leadId, prompt);
      setLeads((prevLeads) =>
        prevLeads.map((l) => (l.id === leadId ? updatedLead : l))
      );
      // Clear specific prompt after successful evaluation
      setReevalPrompts((prev) => {
        const next = { ...prev };
        delete next[leadId];
        return next;
      });
    } catch (err) {
      console.error("Error re-evaluating lead:", err);
      alert("Virhe tekoälyarvioinnissa. Yritä uudelleen.");
    } finally {
      setReevalLoadingId(null);
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case "PENDING":
        return "Jonossa";
      case "HARVESTING":
        return "Haravoidaan";
      case "EVALUATING":
        return "Arvioidaan (AI)";
      case "ENRICHING":
        return "Rikastetaan (AI)";
      case "REVIEW":
        return "Tarkastettavana";
      case "COMPLETED":
        return "Valmis";
      default:
        return status || "Tuntematon";
    }
  };

  const getStatusClass = (status?: string) => {
    switch (status) {
      case "PENDING":
        return classes.StatusPending;
      case "HARVESTING":
      case "EVALUATING":
      case "ENRICHING":
        return classes.StatusActive;
      case "REVIEW":
        return classes.StatusReview;
      case "COMPLETED":
        return classes.StatusCompleted;
      default:
        return "";
    }
  };

  const mapScoreToGrade = (score?: string | null): string => {
    if (!score) return "?";
    const upper = score.toUpperCase();
    if (upper === "ACCEPT" || upper === "A") return "A";
    if (upper === "MAYBE" || upper === "B") return "B";
    if (upper === "REJECT" || upper === "D" || upper === "F") return "D";
    return upper;
  };

  const getScoreBadgeClass = (score?: string | null) => {
    if (!score) return classes.ScoreMuted;
    const grade = mapScoreToGrade(score);
    if (grade === "A") return classes.ScoreA;
    if (grade === "B") return classes.ScoreB;
    if (grade === "C") return classes.ScoreC;
    return classes.ScoreD;
  };

  return (
    <div className={classes.PipelineTabContainer}>
      {/* HEADER SECTION */}
      <div className={classes.TabHeader}>
        <div className={classes.TabTitleBlock}>
          <h2>Agenttihaut & Tehotehtävät (AI Pipelines)</h2>
          <p>
            Automatisoidut haravointi-, evaluointi- ja rikastamisajot. Järjestelmä hakee palveluntarjoajia Google Placesista ja YTJ:stä, arvioi heidät Vertex AI:lla ja rikastuttaa yhteystiedot automaattisesti.
          </p>
        </div>
        <div className={classes.TabActions}>
          <button
            className={classes.PrimaryBtn}
            onClick={() => setShowNewForm(!showNewForm)}
          >
            <FontAwesomeIcon icon={faPlus} /> Uusi hakuajo
          </button>
          <button className={classes.SecondaryBtn} onClick={fetchPipelines} disabled={loadingPipelines}>
            <FontAwesomeIcon icon={faSync} className={loadingPipelines ? classes.SpinnerIcon : ""} /> Päivitä
          </button>
        </div>
      </div>

      {/* NEW PIPELINE FORM MODAL/DRAWER */}
      {showNewForm && (
        <div className={classes.FormCard}>
          <h3>Käynnistä uusi agenttihaku</h3>
          <form onSubmit={handleStartPipeline} className={classes.PipelineForm}>
            {formError && <div className={classes.ErrorMsg}>{formError}</div>}
            
            <div className={classes.FormGrid}>
              <div className={classes.FormGroup}>
                <label>
                  Valitse Tietoalusta (Knowledge Base) <span className={classes.Required}>*</span>
                </label>
                <select
                  value={serviceToken}
                  onChange={(e) => setServiceToken(e.target.value)}
                  required
                >
                  <option value="">-- Valitse tietoalusta --</option>
                  {knowledgeBases.map((kb) => {
                    const linkedSvc = services.find(
                      (s) => s.id === kb.serviceId || (kb.serviceToken && s.serviceToken === kb.serviceToken)
                    );
                    return (
                      <option key={kb.id} value={kb.serviceToken || ""}>
                        {kb.verticalName || "Nimetön tietoalusta"} ({kb.serviceToken}){linkedSvc ? ` (Palvelu: ${linkedSvc.name})` : " (Itsenäinen toimiala)"}
                      </option>
                    );
                  })}
                </select>
                {serviceToken && (
                  <span className={classes.FormHelper} style={{ color: "#16a34a", fontWeight: 600, display: "block", marginTop: "4px" }}>
                    ✓ Tietoalustan (Knowledge Base) määritykset injektoidaan ja otetaan automaattisesti käyttöön!
                  </span>
                )}
                {!serviceToken && (
                  <span className={classes.FormHelper}>Valitse tietoalusta, jonka kriteerejä käytetään haun ja evaluoinnin tukena.</span>
                )}
              </div>

              <div className={classes.FormGroup}>
                <label>Aluerajaus (Kaupungit)</label>
                <input
                  type="text"
                  placeholder="Esim. Helsinki, Espoo, Vantaa"
                  value={regions}
                  onChange={(e) => setRegions(e.target.value)}
                />
                <span className={classes.FormHelper}>Pilkuilla eroteltuna. Jätä tyhjäksi käyttääksesi KB:n oletuksia.</span>
              </div>

              <div className={classes.FormGroup}>
                <label>TOL-toimialakoodit</label>
                <input
                  type="text"
                  placeholder="Esim. 43210, 43220"
                  value={tolCodes}
                  onChange={(e) => setTolCodes(e.target.value)}
                />
                <span className={classes.FormHelper}>YTJ-hakuun. Jätä tyhjäksi käyttääksesi KB:n koodilistaa.</span>
              </div>

              <div className={classes.FormGroup}>
                <label>Google Places Hakulausekkeet</label>
                <input
                  type="text"
                  placeholder='Esim. "sähköasennus", "autolatauspiste asennus"'
                  value={placesQueries}
                  onChange={(e) => setPlacesQueries(e.target.value)}
                />
                <span className={classes.FormHelper}>Places-haravointiin. Jätä tyhjäksi käyttääksesi KB:n hakuja.</span>
              </div>

              <div className={classes.FormGroupFull}>
                <label>Mukautetut AI-arviointikriteerit</label>
                <textarea
                  placeholder="Esim. 'Ehdokkaan on tarjottava sähköautojen latauspisteiden asennusta omakotitaloihin...'"
                  value={evaluationCriteria}
                  onChange={(e) => setEvaluationCriteria(e.target.value)}
                  rows={3}
                />
                <span className={classes.FormHelper}>Ylikirjoittaa Knowledge Basen evaluointisäännöt tässä ajossa.</span>
              </div>
            </div>

            <div className={classes.FormButtons}>
              <button
                type="button"
                className={classes.CancelBtn}
                onClick={() => setShowNewForm(false)}
                disabled={startingPipeline}
              >
                Peruuta
              </button>
              <button type="submit" className={classes.ConfirmBtn} disabled={startingPipeline}>
                {startingPipeline ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} /> Käynnistetään...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} /> Käynnistä tehotehtävä
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* PIPELINES LIST GRID */}
      {loadingPipelines && pipelines.length === 0 ? (
        <div className={classes.Spinner}>
          <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} /> Ladataan hakuajoja...
        </div>
      ) : pipelinesError ? (
        <div className={classes.ErrorMsg}>{pipelinesError}</div>
      ) : pipelines.length === 0 ? (
        <div className={classes.EmptyState}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={classes.EmptyIcon} />
          <p>Ei aiempia hakuajoja. Käynnistä ensimmäinen ajo oikeasta yläkulmasta!</p>
        </div>
      ) : (
        <div className={classes.PipelineSection}>
          <h3 className={classes.SectionTitleSmall}>Aktiiviset ja aiemmat hakuajot</h3>
          <div className={classes.PipelineGrid}>
            {pipelines.map((pipeline) => {
              const isSelected = selectedPipelineId === pipeline.id;
              return (
                <div
                  key={pipeline.id}
                  className={`${classes.PipelineCard} ${isSelected ? classes.PipelineCardSelected : ""}`}
                  onClick={() => setSelectedPipelineId(pipeline.id || null)}
                >
                  <div className={classes.CardHeader}>
                    <div className={classes.CardTitleGroup}>
                      <span className={classes.ServiceBadge}>{pipeline.serviceToken}</span>
                      <span className={classes.CardDate}>
                        {pipeline.created ? new Date(pipeline.created).toLocaleString("fi-FI") : "-"}
                      </span>
                    </div>
                    <span className={`${classes.StatusBadge} ${getStatusClass(pipeline.status)}`}>
                      {getStatusLabel(pipeline.status)}
                    </span>
                  </div>

                  <div className={classes.MetricsGrid}>
                    <div className={classes.MetricItem}>
                      <span className={classes.MetricLabel}>Haravoitu</span>
                      <span className={classes.MetricVal}>{pipeline.statHarvested ?? 0}</span>
                    </div>
                    <div className={classes.MetricItem}>
                      <span className={classes.MetricLabel}>Evaluointi</span>
                      <span className={classes.MetricVal}>{pipeline.statEvaluated ?? 0}</span>
                    </div>
                    <div className={classes.MetricItem}>
                      <span className={classes.MetricLabel}>Rikastettu</span>
                      <span className={classes.MetricVal}>{pipeline.statEnriched ?? 0}</span>
                    </div>
                    <div className={classes.MetricItem}>
                      <span className={classes.MetricLabel}>Lisätty</span>
                      <span className={classes.MetricVal}>{pipeline.statPromoted ?? 0}</span>
                    </div>
                  </div>

                  <div className={classes.ScoresSummary}>
                    <span className={`${classes.MiniScore} ${classes.ScoreA}`}>
                      A: {pipeline.statAccepted ?? 0}
                    </span>
                    <span className={`${classes.MiniScore} ${classes.ScoreB}`}>
                      B: {pipeline.statMaybe ?? 0}
                    </span>
                    <span className={`${classes.MiniScore} ${classes.ScoreD}`}>
                      Hylätty: {pipeline.statRejected ?? 0}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SELECTED PIPELINE LEADS LIST */}
      {selectedPipelineId && (
        <div className={classes.PipelineLeadsSection}>
          <div className={classes.LeadsHeader}>
            <div className={classes.LeadsTitleGroup}>
              <h3>Ehdokkaat & AI-evaluointitulokset</h3>
              <span className={classes.SelectedLabel}>
                Valittu ajo ID: <span className={classes.CodeText}>{selectedPipelineId}</span>
              </span>
            </div>
            
            <div className={classes.FiltersBlock}>
              <span className={classes.FilterLabel}>Suodata arvosanan mukaan:</span>
              <div className={classes.FilterButtons}>
                <button
                  className={`${classes.FilterBtn} ${scoreFilter === "" ? classes.FilterBtnActive : ""}`}
                  onClick={() => setScoreFilter("")}
                >
                  Kaikki
                </button>
                <button
                  className={`${classes.FilterBtn} ${classes.ScoreA} ${scoreFilter === "A" ? classes.FilterBtnActive : ""}`}
                  onClick={() => setScoreFilter("A")}
                >
                  A (Sopii erinomaisesti)
                </button>
                <button
                  className={`${classes.FilterBtn} ${classes.ScoreB} ${scoreFilter === "B" ? classes.FilterBtnActive : ""}`}
                  onClick={() => setScoreFilter("B")}
                >
                  B (Sopii varauksella)
                </button>
                <button
                  className={`${classes.FilterBtn} ${classes.ScoreC} ${scoreFilter === "C" ? classes.FilterBtnActive : ""}`}
                  onClick={() => setScoreFilter("C")}
                >
                  C (Ei suositella)
                </button>
                <button
                  className={`${classes.FilterBtn} ${classes.ScoreD} ${scoreFilter === "D" ? classes.FilterBtnActive : ""}`}
                  onClick={() => setScoreFilter("D")}
                >
                  D / F (Hylätty)
                </button>
              </div>
            </div>
          </div>

          {selectedPipeline && (
            <div className={classes.PipelineMetaCard}>
              <div className={classes.MetaRow}>
                <span className={classes.MetaLabel}>Käytetty tietoalusta (KB):</span>
                <span className={classes.MetaValue}>
                  {onNavigateToKb ? (
                    <button
                      className={classes.LinkBtn}
                      onClick={() => onNavigateToKb(selectedPipeline.serviceToken || "")}
                    >
                      <FontAwesomeIcon icon={faBookOpen} style={{ marginRight: "6px" }} />
                      {selectedPipeline.serviceToken}
                    </button>
                  ) : (
                    <span>{selectedPipeline.serviceToken}</span>
                  )}
                </span>
              </div>
              <div className={classes.MetaGrid}>
                {selectedPipeline.regions && (
                  <div className={classes.MetaItem}>
                    <span className={classes.MetaLabel}>Aluerajaus:</span>
                    <span className={classes.MetaValue}>{selectedPipeline.regions}</span>
                  </div>
                )}
                {selectedPipeline.tolCodes && (
                  <div className={classes.MetaItem}>
                    <span className={classes.MetaLabel}>TOL-toimialat:</span>
                    <span className={classes.MetaValue}>{selectedPipeline.tolCodes}</span>
                  </div>
                )}
                {selectedPipeline.placesQueries && (
                  <div className={classes.MetaItem}>
                    <span className={classes.MetaLabel}>Google Places haut:</span>
                    <span className={classes.MetaValue}>{selectedPipeline.placesQueries}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {loadingLeads ? (
            <div className={classes.Spinner}>
              <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} /> Ladataan ehdokkaita...
            </div>
          ) : leadsError ? (
            <div className={classes.ErrorMsg}>{leadsError}</div>
          ) : leads.length === 0 ? (
            <div className={classes.EmptyState}>
              <FontAwesomeIcon icon={faExclamationTriangle} className={classes.EmptyIcon} />
              <p>Ei ehdokkaita valituilla hakuehdoilla tälle ajolle.</p>
            </div>
          ) : (
            <div className={classes.LeadsList}>
              {leads.map((lead) => {
                const isExpanded = expandedLeadId === lead.id;
                const isApproved = !!lead.linkedOrgId || lead.status === "CONTRACT";
                
                return (
                  <div
                    key={lead.id}
                    className={`${classes.LeadRowCard} ${isApproved ? classes.LeadApproved : ""}`}
                  >
                    <div className={classes.LeadRowHeader} onClick={() => setExpandedLeadId(isExpanded ? null : lead.id)}>
                      <div className={classes.LeadMainInfo}>
                        <div className={`${classes.ScoreBadge} ${getScoreBadgeClass(lead.aiScore)}`}>
                          {mapScoreToGrade(lead.aiScore)}
                        </div>
                        <div className={classes.LeadTitleGroup}>
                          <h4 className={classes.LeadName}>
                            {lead.ytjName || lead.placeName || "Nimetön yritys"}
                            {lead.isToiminimi && <span className={classes.TmiBadge}>Tmi / Sole Prop</span>}
                            {!lead.isToiminimi && lead.companyForm && <span className={classes.OyBadge}>{lead.companyForm}</span>}
                          </h4>
                          <span className={classes.LeadMeta}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} /> {lead.city || lead.street || "Sijainti tuntematon"}
                            {lead.businessId && (
                              <span className={classes.DividerDot}>• Y-tunnus: {lead.businessId}</span>
                            )}
                          </span>
                        </div>
                      </div>

                      <div className={classes.LeadRowActions} onClick={(e) => e.stopPropagation()}>
                        {isApproved ? (
                          <div className={classes.OnboardedIndicator}>
                            <FontAwesomeIcon icon={faCheck} /> Aktivoitu kumppaniksi
                          </div>
                        ) : (
                          <div className={classes.ActionButtons}>
                            <button
                              className={classes.ApproveBtn}
                              onClick={() => handleApproveLead(lead.id)}
                              disabled={actionLoadingId === lead.id}
                            >
                              {actionLoadingId === lead.id ? (
                                <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} />
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faCheck} /> Hyväksy & Onboardaa
                                </>
                              )}
                            </button>
                            <button
                              className={classes.RejectBtn}
                              onClick={() => handleRejectLead(lead.id)}
                              disabled={actionLoadingId === lead.id}
                            >
                              {actionLoadingId === lead.id ? (
                                <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} />
                              ) : (
                                <FontAwesomeIcon icon={faTimes} />
                              )}
                            </button>
                          </div>
                        )}
                        <FontAwesomeIcon
                          icon={isExpanded ? faAngleUp : faAngleDown}
                          className={classes.ExpandArrow}
                        />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className={classes.LeadDetailsBlock}>
                        <div className={classes.DetailsSplitGrid}>
                          {/* AI Evaluation */}
                          <div className={classes.LeadDetailSection}>
                            <h5>
                              <FontAwesomeIcon icon={faBuilding} /> AI Arviointiraportti
                            </h5>
                            <div className={classes.AiReasoningText}>
                              {lead.aiReason || "Ei AI-arviointiperusteluita saatavilla."}
                            </div>
                            {lead.certifications && (
                              <div className={classes.CertificationsList}>
                                <strong>Löydetyt sertifikaatit / pätevyydet:</strong>
                                <span className={classes.CertBadge}>{lead.certifications}</span>
                              </div>
                            )}

                            {/* Re-evaluation Input Section */}
                            <div className={classes.ReevaluateSection}>
                              <h6>Tee AI-analyysi uudelleen mukautetulla lisäohjeella:</h6>
                              <div className={classes.ReevaluateInputGroup}>
                                <textarea
                                  placeholder="Esim. 'Meiltä myös turvalliset puunkaadot löytyy alalaidasta' tai 'Yritys tekee sähköasennuksia'"
                                  value={reevalPrompts[lead.id] || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setReevalPrompts((prev) => ({
                                      ...prev,
                                      [lead.id]: val,
                                    }));
                                  }}
                                  rows={2}
                                />
                                <button
                                  className={classes.ReevalBtn}
                                  onClick={() => handleReevaluateLead(lead.id)}
                                  disabled={reevalLoadingId === lead.id}
                                >
                                  {reevalLoadingId === lead.id ? (
                                    <>
                                      <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} /> Analysoidaan...
                                    </>
                                  ) : (
                                    <>
                                      <FontAwesomeIcon icon={faSync} /> Analysoi uudelleen (AI)
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Contact & GDPR Boundaries */}
                          <div className={classes.LeadDetailSection}>
                            <h5>Yhteystiedot & GDPR-rajat</h5>
                            <div className={classes.ContactSpecList}>
                              {lead.phone && (
                                <div className={classes.SpecItem}>
                                  <FontAwesomeIcon icon={faPhone} /> <span>{lead.phone}</span>
                                </div>
                              )}
                              <div className={classes.SpecItem}>
                                <FontAwesomeIcon icon={faEnvelope} />{" "}
                                <span className={lead.gdprRestrictionColdEmail ? classes.GdprMutedEmail : ""}>
                                  {lead.email || "Ei sähköpostia"}
                                </span>
                              </div>
                              {lead.website && (
                                <div className={classes.SpecItem}>
                                  <FontAwesomeIcon icon={faGlobe} />{" "}
                                  <a href={lead.website} target="_blank" rel="noreferrer">
                                    {lead.website}
                                  </a>
                                </div>
                              )}
                            </div>

                            {/* GDPR Warning Shield */}
                            {lead.gdprRestrictionColdEmail && (
                              <div className={classes.GdprShield}>
                                <FontAwesomeIcon icon={faShieldAlt} />
                                <div>
                                  <strong>GDPR Cold Outreach Restriction</strong>
                                  <p>
                                    Toiminimi (yksityinen elinkeinonharjoittaja). Automaattinen kylmäsähköposti on estetty ja poistettu käytöstä tietosuojan turvaamiseksi. Ota yhteyttä vain manuaalisesti puhelimitse.
                                  </p>
                                </div>
                              </div>
                            )}

                            {lead.linkedOrgId && (
                              <div className={classes.LinkedOrgBox}>
                                <span>Kumppani ID:</span>{" "}
                                <span className={classes.CodeText}>{lead.linkedOrgId}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {onSelectLead && (
                          <div className={classes.ActionFooter}>
                            <button
                              className={classes.SecondaryBtnSmall}
                              onClick={() => onSelectLead(lead)}
                            >
                              Avaa manuaalisessa onboarding-näkymässä <FontAwesomeIcon icon={faSearch} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
